import React, { useEffect, useState } from 'react'
import {Eye, EyeOff, Check, X, Trash2, Search, Lock, Globe, Upload, Music} from 'lucide-react'
import { useEntityCRUD } from '../../hooks/useEntityCRUD'
import { useAdminLog } from '../../hooks/useAdminLog'
import AdminLayout from '../../components/admin/AdminLayout'
import { lumi } from '../../lib/lumi'
import toast from 'react-hot-toast'

const PrayersManagement: React.FC = () => {
  const { data: prayers, loading, fetchData, updateRecord, deleteRecord } = useEntityCRUD('prayers')
  const { logAction } = useAdminLog()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('ALL')

  useEffect(() => {
    const filter: any = {}
    if (statusFilter !== 'ALL') {
      filter.status = statusFilter
    } else {
      // Não mostrar pedidos DELETED por padrão
      filter.status = { $ne: 'DELETED' }
    }
    fetchData({ filter, sort: { createdAt: -1 } })
  }, [statusFilter])

  const handleApprove = async (id: string) => {
    try {
      await updateRecord(id, { status: 'APPROVED', updatedAt: new Date().toISOString() })
      await logAction('APPROVE', 'prayers', id, 'Prayer approved')
      toast.success('Oração aprovada!')
    } catch (error) {
      console.error('Failed to approve:', error)
    }
  }

  const handleHide = async (id: string) => {
    try {
      await updateRecord(id, { status: 'HIDDEN', updatedAt: new Date().toISOString() })
      await logAction('UPDATE', 'prayers', id, 'Prayer hidden')
      toast.success('Oração ocultada!')
    } catch (error) {
      console.error('Failed to hide:', error)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta oração?')) return
    
    try {
      await deleteRecord(id)
      await logAction('DELETE', 'prayers', id, 'Prayer deleted')
    } catch (error) {
      console.error('Failed to delete:', error)
    }
  }

  const handleAudioUpload = async (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const audioFile = files[0]
    
    // Validar tipo de arquivo
    if (!audioFile.type.startsWith('audio/')) {
      toast.error('Por favor, selecione um arquivo de áudio válido')
      return
    }

    try {
      toast.loading('Enviando áudio...', { id: 'upload' })
      
      const results = await lumi.tools.file.upload([audioFile])
      const uploadResult = results[0]
      
      if (uploadResult.uploadError) {
        throw new Error(uploadResult.uploadError)
      }
      
      if (uploadResult.fileUrl) {
        await updateRecord(id, { 
          responseAudioUrl: uploadResult.fileUrl,
          updatedAt: new Date().toISOString() 
        })
        await logAction('UPDATE', 'prayers', id, 'Audio response uploaded')
        toast.success('Áudio enviado com sucesso!', { id: 'upload' })
      }
    } catch (error) {
      console.error('Failed to upload audio:', error)
      toast.error('Erro ao enviar áudio. Tente novamente.', { id: 'upload' })
    }
  }

  const filteredPrayers = prayers.filter(prayer =>
    prayer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prayer.message?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getStatusBadge = (status: string) => {
    const styles = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      APPROVED: 'bg-green-100 text-green-800',
      HIDDEN: 'bg-gray-100 text-gray-800',
      DELETED: 'bg-red-100 text-red-800'
    }
    return styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800'
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gerenciar Orações</h1>
        <p className="text-gray-600">Modere e gerencie os pedidos de oração</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por nome ou mensagem..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
          >
            <option value="ALL">Todos os Status</option>
            <option value="PENDING">Pendentes</option>
            <option value="APPROVED">Aprovados</option>
            <option value="HIDDEN">Ocultos</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPrayers.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              Nenhuma oração encontrada
            </div>
          ) : (
            filteredPrayers.map((prayer: any) => (
              <div key={prayer._id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{prayer.name}</h3>
                      {prayer.type && (
                        <span className="px-2.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                          {prayer.type}
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(prayer.status)}`}>
                        {prayer.status}
                      </span>
                      {prayer.isPrivate ? (
                        <span className="flex items-center gap-1 text-xs text-red-600">
                          <Lock size={14} /> Sigiloso
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <Globe size={14} /> Público
                        </span>
                      )}
                    </div>
                    {prayer.email && (
                      <p className="text-sm text-gray-500 mb-2">{prayer.email}</p>
                    )}
                    <p className="text-gray-700 whitespace-pre-wrap">{prayer.message}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {new Date(prayer.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>

                {prayer.responseAudioUrl && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Music size={16} className="text-green-600" />
                      <span className="text-sm font-semibold text-green-800">Áudio de Resposta Enviado</span>
                    </div>
                    <audio controls className="w-full">
                      <source src={prayer.responseAudioUrl} />
                      Seu navegador não suporta o elemento de áudio.
                    </audio>
                  </div>
                )}

                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  {prayer.status === 'PENDING' && (
                    <>
                      <button
                        onClick={() => handleApprove(prayer._id)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                      >
                        <Check size={16} /> Aprovar
                      </button>
                      <button
                        onClick={() => handleHide(prayer._id)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        <EyeOff size={16} /> Ocultar
                      </button>
                    </>
                  )}
                  {prayer.status === 'APPROVED' && (
                    <button
                      onClick={() => handleHide(prayer._id)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      <EyeOff size={16} /> Ocultar
                    </button>
                  )}
                  {prayer.status === 'HIDDEN' && (
                    <button
                      onClick={() => handleApprove(prayer._id)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      <Eye size={16} /> Mostrar
                    </button>
                  )}
                  <label className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors cursor-pointer">
                    <Upload size={16} />
                    {prayer.responseAudioUrl ? 'Substituir Áudio' : 'Enviar Áudio'}
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => handleAudioUpload(prayer._id, e)}
                      className="hidden"
                    />
                  </label>
                  <button
                    onClick={() => handleDelete(prayer._id)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors ml-auto"
                  >
                    <Trash2 size={16} /> Excluir
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </AdminLayout>
  )
}

export default PrayersManagement